// Global Instructions Rule Applied!
//
// Company Brief card (A2, owner-approved mockup v4): the Dashboard's
// narrative, derived from agent memory per (account, section) —
// cfg.company_brief_sections drives the section set/order. Read-only by
// design; per-section "Correct" opens the correction-via-memory modal.
import React, { useCallback, useEffect, useState } from 'react'
import { Tag } from 'antd'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchCompanyBrief } from '../services/internalAgentsService.ts'
import { CompanyBriefSection } from '../../../state_manager/data_models/internal_agents_model.ts'
import { BriefCorrectionModal } from './BriefCorrectionModal.tsx'

interface CompanyBriefCardProps {
  accountId: string  /** Hands `load` to the company form so its Refresh button reloads this
   *  panel. Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

export const CompanyBriefCard: React.FC<CompanyBriefCardProps> = ({
  accountId,
  onRefreshReady = null
}) => {
  const [sections, setSections] = useState<CompanyBriefSection[]>([])
  const [loading, setLoading] = useState(true)
  const [correcting, setCorrecting] = useState<CompanyBriefSection | null>(null)
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setSections(await fetchCompanyBrief(accountId, token()))
    } catch (error) {
      console.error('[internal-agents] company brief load failed:', error)
      setSections([])
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale. `onRefreshReady` hands `load` up to the
  // company form so its Refresh button reloads this panel too (same contract as
  // DocumentsTab / FileUploadManager).
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  const written = sections.filter((s) => s.company_briefs__content)
  const generatedAt = written.reduce<string | null>(
    (latest, s) =>
      s.company_briefs__generated_at && (!latest || s.company_briefs__generated_at > latest)
        ? s.company_briefs__generated_at
        : latest,
    null
  )

  return (
    <FormSection
      data-component='CompanyBriefCard'
      title='Company Brief'
      titleColor='green'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerActions={
        <span className='flex items-center gap-2'>
          <Tag color='blue'>Agent-written</Tag>
          {generatedAt ? (
            <span className='text-gray-400 text-[11.5px]'>
              updated {dayjs(generatedAt).format('MM/DD/YYYY')}
            </span>
          ) : null}
        </span>
      }
    >
      {written.length === 0 && !loading ? (
        <div className='text-gray-400 text-xs py-2'>Awaiting first deep run.</div>
      ) : (
        <div className='flex flex-col divide-y divide-gray-100 -mx-1'>
          {sections.map((section) => (
            <div key={section.company_brief_sections__section_key} className='px-1 py-2'>
              <div className='flex items-baseline gap-2'>
                <span className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500'>
                  {section.company_brief_sections__label}
                </span>
                <button
                  type='button'
                  className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left ml-auto text-[11.5px]'
                  onClick={() => setCorrecting(section)}
                >
                  Correct
                </button>
              </div>
              {section.company_briefs__content ? (
                <div className='whitespace-pre-line text-[13px] mt-0.5'>
                  {section.company_briefs__content}
                </div>
              ) : (
                <div className='text-gray-400 text-xs mt-0.5'>Awaiting first deep run.</div>
              )}
            </div>
          ))}
        </div>
      )}
      <BriefCorrectionModal
        open={correcting != null}
        accountId={accountId}
        sectionKey={correcting?.company_brief_sections__section_key ?? null}
        sectionLabel={correcting?.company_brief_sections__label ?? null}
        onClose={() => setCorrecting(null)}
        onCorrected={load}
      />
    </FormSection>
  )
}
